import { NodeType } from "types/misc";

/**
 * Pure path/leaf helpers for hierarchical object references.
 *
 * This module deliberately has no dependency on data-fetching utilities
 * (`objectData`/`search`) so it can be imported from anywhere without creating
 * an import cycle.
 */

/**
 * Fake object id used by the synthetic ancestor nodes of the filtered (search-result)
 * tree — a path that has to be shown because a match sits under it, but that the search
 * itself did not return an object for.
 *
 * Lives here so both the tree that builds such nodes and the readers that must refuse to
 * ask a backend about them can see it without either importing the other.
 */
export const SYNTHETIC_NODE_ID = -1;

export function getFilePathFromObjectPath(objectPath: string): string | null {
    //https://novorender.com/formats-integrations/
    const match = objectPath.match(
        /^(?<path>.+\.(dem|dwg|dxf|ifc|xml|kof|nwd|nwc|obj|pdms|rvm|rvt|step|stp|wms|wmts|pts|las|laz|e57|jpg|jpeg|tif|tiff|pdf))(?=\/|$)/i,
    )?.groups;

    if (!match || !match.path) {
        return null;
    }

    return match.path;
}

/**
 * Like {@link getFilePathFromObjectPath}, but returns the outermost/topmost file
 * node in the path rather than the innermost one (relevant for nested containers,
 * e.g. an object inside a .nwc that is itself federated inside a .nwd).
 */
export function getTopmostFilePathFromObjectPath(objectPath: string): string | null {
    const match = objectPath.match(
        /^(?<path>.+?\.(dem|dwg|dxf|ifc|xml|kof|nwd|nwc|obj|pdms|rvm|rvt|step|stp|wms|wmts|pts|las|laz|e57|jpg|jpeg|tif|tiff|pdf))(?=\/|$)/i,
    )?.groups;
    if (!match || !match.path) {
        return null;
    }
    return match.path;
}

/**
 * Parent-path prefixes for a hierarchy path. For `a/b/c`, yields `""`, `a`, and
 * `a/b` by default; this is not a simple slash split.
 */
export function* getParentPaths(path: string, includeRoot = true): Generator<string> {
    let pos = 0;
    if (path !== "" && includeRoot) {
        yield "";
    }
    while (pos !== -1) {
        const nextPos = path.indexOf("/", pos);
        if (nextPos === -1) {
            break;
        }
        yield path.slice(0, nextPos);
        pos = nextPos + 1;
    }
}

/**
 * The negative `descentDepth` for a `db.search({ parentPath: path, descentDepth })` that walks
 * the whole ancestor chain of `path` up to (and including) the file node in a single round-trip.
 * The depth is the number of path segments below the file, so a click at any depth costs one search.
 */
export function getAncestorSearchDepth(path: string): number {
    const filePath = getFilePathFromObjectPath(path);
    const subfilePath = filePath ? path.substring(filePath.length + 1) : path;
    let slashCount = 0;
    for (const c of subfilePath) {
        if (c === "/") {
            slashCount++;
        }
    }
    return -(slashCount + 1);
}

/**
 * File formats whose file-level node is a container of hierarchical content.
 *
 * Some parsers (notably DWG/DXF) report the file node itself with `NodeType.Leaf`
 * even though it has children. Without this override the model tree renders such a
 * file as a non-expandable leaf, so its content never appears nested under the file
 * node (it looks disconnected from the file root). See BM-10439 (and BM-11140).
 */
const CONTAINER_FILE_EXTENSIONS = new Set([
    "dwg",
    "dxf",
    "ifc",
    "nwd",
    "nwc",
    "rvm",
    "rvt",
    "obj",
    "pdms",
    "step",
    "stp",
]);

/**
 * Returns true when `path` points to the file-level node of a container format.
 * Such nodes must always be treated as expandable in the model tree, regardless
 * of the `NodeType` reported by the data layer.
 */
export function isContainerFileNode(path: string): boolean {
    // Fast-path: a container file node is a path that ends with a known container
    // extension, and where that extension is on the final path segment.
    const lastSlash = path.lastIndexOf("/");
    const lastDot = path.lastIndexOf(".");

    if (lastDot === -1 || lastDot < lastSlash) {
        return false;
    }

    const ext = path.slice(lastDot + 1).toLowerCase();
    return CONTAINER_FILE_EXTENSIONS.has(ext);
}

/**
 * Resolves whether a hierarchical object should be treated as a leaf node in the
 * model tree. Container file-level nodes are never leaves even when the data layer
 * reports them as such.
 */
export function isLeafNode(obj: { path: string; type: number }): boolean {
    return obj.type === NodeType.Leaf && !isContainerFileNode(obj.path);
}

/**
 * Property key carrying the format-agnostic node classification emitted by the
 * BIM parsers. Surfaced in `metadata.properties` as
 * `["Procore/NodeClass", "<value>"]`. See BM-11218 / BM-11226.
 */
export const NODE_CLASS_PROPERTY = "Procore/NodeClass";

/**
 * Format-agnostic node classification.
 *
 * `File` is a special kind of `Container` (the outermost container / file root) and
 * is treated identically to `Container` for selection resolution.
 */
export enum NodeClass {
    File = "file",
    Container = "container",
    Object = "object",
    Fragment = "fragment",
}

/**
 * Reads {@link NODE_CLASS_PROPERTY} off an object's properties, if present.
 * Returns `undefined` for legacy objects parsed before the property existed (or
 * with an unrecognized value), which callers use to fall back to legacy behavior.
 */
export function getNodeClass(properties: readonly (readonly [string, string])[] | undefined): NodeClass | undefined {
    if (!properties) {
        return undefined;
    }

    const key = NODE_CLASS_PROPERTY.toLowerCase();
    const entry = properties.find(([k]) => k.toLowerCase() === key);
    if (!entry) {
        return undefined;
    }

    switch (entry[1].toLowerCase()) {
        case NodeClass.File:
            return NodeClass.File;
        case NodeClass.Container:
            return NodeClass.Container;
        case NodeClass.Object:
            return NodeClass.Object;
        case NodeClass.Fragment:
            return NodeClass.Fragment;
        default:
            return undefined;
    }
}
